import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, Check, ShieldCheck, Truck, Globe2 } from "lucide-react";
import RFQDialog from "@/components/RFQDialog";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { pickLanguage } from "@/lib/publicLanguage";
import { localizeProduct } from "@/lib/catalogLocalization";
import { fetchPublicProductBySlug, usePublicCategories } from "@/hooks/usePublicSiteData";

export default function ProductDetail() {
  const { slug } = useParams();
  const [rfqOpen, setRfqOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const { language } = useSiteLanguage();
  const { data: categories = [] } = usePublicCategories();
  const copy = {
    loading: pickLanguage(language, {
      en: "Loading product…",
      "zh-CN": "正在加载产品……",
      bn: "পণ্য লোড হচ্ছে…",
    }),
    notFound: pickLanguage(language, {
      en: "Product not found.",
      "zh-CN": "未找到该产品。",
      bn: "পণ্যটি পাওয়া যায়নি।",
    }),
    backToCatalog: pickLanguage(language, {
      en: "Back to catalog",
      "zh-CN": "返回目录",
      bn: "ক্যাটালগে ফিরে যান",
    }),
    verified: pickLanguage(language, {
      en: "Verified",
      "zh-CN": "已核验",
      bn: "যাচাইকৃত",
    }),
    moq: pickLanguage(language, {
      en: "MOQ",
      "zh-CN": "起订量",
      bn: "সর্বনিম্ন অর্ডার",
    }),
    priceRange: pickLanguage(language, {
      en: "Price Range",
      "zh-CN": "价格区间",
      bn: "মূল্য সীমা",
    }),
    hsCode: pickLanguage(language, {
      en: "HS Code",
      "zh-CN": "HS 编码",
      bn: "HS কোড",
    }),
    origin: pickLanguage(language, {
      en: "Origin",
      "zh-CN": "原产地",
      bn: "উৎপত্তি দেশ",
    }),
    onQuote: pickLanguage(language, {
      en: "On Quote",
      "zh-CN": "询价获取",
      bn: "কোটেশন সাপেক্ষে",
    }),
    requestQuote: pickLanguage(language, {
      en: "Request Quote",
      "zh-CN": "申请报价",
      bn: "কোটেশন অনুরোধ করুন",
    }),
    talkToTradeDesk: pickLanguage(language, {
      en: "Talk to Trade Desk",
      "zh-CN": "联系贸易团队",
      bn: "ট্রেড ডেস্কের সাথে কথা বলুন",
    }),
    preShipmentInspection: pickLanguage(language, {
      en: "Pre-shipment inspection",
      "zh-CN": "装运前检验",
      bn: "চালান-পূর্ব পরিদর্শন",
    }),
    doorToDoor: pickLanguage(language, {
      en: "Door-to-door logistics",
      "zh-CN": "门到门物流",
      bn: "ডোর-টু-ডোর লজিস্টিকস",
    }),
    flexiblePayment: pickLanguage(language, {
      en: "Flexible payment terms",
      "zh-CN": "灵活付款条件",
      bn: "নমনীয় পেমেন্ট শর্ত",
    }),
    productDetails: pickLanguage(language, {
      en: "Product details",
      "zh-CN": "产品详情",
      bn: "পণ্যের বিস্তারিত",
    }),
    specifications: pickLanguage(language, {
      en: "Specifications",
      "zh-CN": "规格参数",
      bn: "স্পেসিফিকেশন",
    }),
    noSpecifications: pickLanguage(language, {
      en: "No specifications listed.",
      "zh-CN": "暂无规格信息。",
      bn: "কোনো স্পেসিফিকেশন তালিকাভুক্ত নেই।",
    }),
    viewImage: pickLanguage(language, {
      en: "View image",
      "zh-CN": "查看图片",
      bn: "ছবি দেখুন",
    }),
  };

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => fetchPublicProductBySlug(slug),
  });
  const localizedProduct = useMemo(() => localizeProduct(product, language), [product, language]);
  const categoryName = useMemo(() => {
    if (!localizedProduct) return "";
    return (
      categories.find((item) => item.slug === localizedProduct.category_slug)?.name ||
      localizedProduct.category_slug
    );
  }, [categories, localizedProduct]);

  const allImages = useMemo(() => {
    if (!localizedProduct) return [];
    const list = [localizedProduct.image_url, ...(localizedProduct.gallery || [])].filter(Boolean);
    return [...new Set(list)];
  }, [localizedProduct]);
  const mainImage = activeImage || allImages[0];

  if (isLoading) {
    return (
      <div className="container-rsi py-32 text-center text-gray-500">{copy.loading}</div>
    );
  }
  if (isError || !localizedProduct) {
    return (
      <div className="container-rsi py-32 text-center">
        <p className="text-gray-500">{copy.notFound}</p>
        <Link to="/products" className="btn-primary mt-6 inline-flex">
          {copy.backToCatalog}
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="py-16 bg-[var(--rsi-bg-subtle)]" data-testid="product-detail">
        <div className="container-rsi">
          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[var(--rsi-purple)] mb-8"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-4 w-4" /> {copy.backToCatalog}
          </Link>

          <div className="grid lg:grid-cols-12 gap-10">
            {/* Image */}
            <div className="lg:col-span-6">
              <div className="aspect-square rounded-2xl overflow-hidden bg-white rsi-card">
                <img
                  src={mainImage}
                  alt={localizedProduct.name}
                  className="h-full w-full object-cover transition-opacity duration-300"
                  data-testid="product-main-image"
                />
              </div>
              {allImages.length > 1 && (
                <div
                  className={`mt-4 grid gap-3`}
                  style={{ gridTemplateColumns: `repeat(${Math.min(allImages.length, 5)}, minmax(0, 1fr))` }}
                  data-testid="product-gallery-thumbs"
                >
                  {allImages.slice(0, 5).map((g, i) => (
                    <button
                      key={g + i}
                      type="button"
                      onClick={() => setActiveImage(g)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        mainImage === g
                          ? "border-[var(--rsi-purple)] ring-2 ring-[var(--rsi-purple)]/20"
                          : "border-gray-200 hover:border-[var(--rsi-purple)]/50"
                      }`}
                      data-testid={`gallery-thumb-${i}`}
                      aria-label={`${copy.viewImage} ${i + 1}`}
                    >
                      <img src={g} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="badge-navy">{categoryName}</span>
                {localizedProduct.origin_country && (
                  <span className="badge-purple">
                    <Globe2 className="h-3 w-3" /> {localizedProduct.origin_country}
                  </span>
                )}
                <span className="badge-verified">
                  <ShieldCheck className="h-3 w-3" /> {copy.verified}
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-[var(--rsi-navy)] leading-[1.05]">
                {localizedProduct.name}
              </h1>
              <p className="mt-4 text-gray-700 text-lg leading-relaxed">
                {localizedProduct.short_description}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rsi-card p-4">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">{copy.moq}</div>
                  <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">
                    {localizedProduct.moq || "—"}
                  </div>
                </div>
                <div className="rsi-card p-4">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">{copy.priceRange}</div>
                  <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">
                    {localizedProduct.price_range || copy.onQuote}
                  </div>
                </div>
                <div className="rsi-card p-4">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">{copy.hsCode}</div>
                  <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">
                    {localizedProduct.hs_code || "—"}
                  </div>
                </div>
                <div className="rsi-card p-4">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider">{copy.origin}</div>
                  <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">
                    {localizedProduct.origin_country || "—"}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => setRfqOpen(true)}
                  className="btn-primary"
                  data-testid="product-rfq-button"
                >
                  {copy.requestQuote} <ArrowUpRight className="h-4 w-4" />
                </button>
                <Link to="/contact" className="btn-ghost">
                  {copy.talkToTradeDesk}
                </Link>
              </div>

              <div className="mt-10 grid sm:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <ShieldCheck className="h-4 w-4 text-[var(--rsi-green)]" /> {copy.preShipmentInspection}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Truck className="h-4 w-4 text-[var(--rsi-green)]" /> {copy.doorToDoor}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Check className="h-4 w-4 text-[var(--rsi-green)]" /> {copy.flexiblePayment}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description + Specs */}
      <section className="py-16 bg-white">
        <div className="container-rsi grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display font-extrabold text-2xl tracking-tight text-[var(--rsi-navy)]">
              {copy.productDetails}
            </h2>
            <div
              className="mt-4 text-gray-700 leading-relaxed prose-rsi"
              dangerouslySetInnerHTML={{
                __html: localizedProduct.description || localizedProduct.short_description || "",
              }}
            />
          </div>
          <div className="lg:col-span-5">
            <div className="rsi-card p-6">
              <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                {copy.specifications}
              </h3>
              <div className="mt-4 divide-y divide-gray-100">
                {(localizedProduct.specifications || []).length === 0 ? (
                  <div className="text-sm text-gray-500 py-4">{copy.noSpecifications}</div>
                ) : (
                  localizedProduct.specifications.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-3 text-sm">
                      <span className="text-gray-500">{s.key}</span>
                      <span className="font-mono-plex font-medium text-[var(--rsi-navy)]">
                        {s.value}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <RFQDialog open={rfqOpen} onOpenChange={setRfqOpen} product={localizedProduct} />
    </>
  );
}
